import app from 'flarum/common/app';

/**
 * Formatação de moeda sensível ao locale do usuário.
 *
 * - Loja armazena tudo na moeda configurada em `marketplace_currency`
 *   (default BRL). É essa que o Stripe cobra.
 * - Quando o locale do usuário é `en/*`, exibimos como USD usando a taxa
 *   `marketplace_fx_usd` definida pelo admin (BRL → USD).
 * - Outros locales caem no símbolo/separadores configurados no admin.
 *
 * IMPORTANTE: a conversão pra USD é só visual. Disclaimer no checkout
 * deve avisar que a cobrança é feita em BRL pela Stripe.
 */

/** Locale ativo do usuário (ex: 'pt-BR', 'en'). */
function locale() {
  return app.translator?.getLocale?.() || app?.data?.locale || (typeof document !== 'undefined' ? document.documentElement.lang : null) || 'pt-BR';
}

/** True se o locale do usuário é variant de inglês. */
function isEnglish() {
  const loc = locale().toLowerCase();
  return loc === 'en' || loc.startsWith('en-') || loc.startsWith('en_');
}

/** Configuração da loja (vem do backend via Settings/serializeToForum). */
function storeCurrency() {
  return {
    symbol: app.forum.attribute('marketplace_currency_symbol') ?? 'R$',
    code: app.forum.attribute('marketplace_currency') ?? 'BRL',
    position: app.forum.attribute('marketplace_currency_position') ?? 'before',
    decimal: app.forum.attribute('marketplace_decimal_separator') ?? ',',
    thousand: app.forum.attribute('marketplace_thousand_separator') ?? '.'
  };
}

/** Taxa de câmbio para o `target` partindo da moeda da loja. */
function fxRate(target) {
  const store = storeCurrency().code.toUpperCase();
  if (store === target.toUpperCase()) return 1;
  if (target === 'USD') {
    const v = app.forum.attribute('marketplace_fx_usd');
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0.2;
  }
  return 1;
}

/** Decide qual moeda mostrar baseado no locale do usuário. */
function targetCurrency() {
  if (isEnglish()) return 'USD';
  return storeCurrency().code.toUpperCase();
}
export function currency() {
  return storeCurrency();
}
export function formatMoney(amount) {
  const n = Number(amount) || 0;
  const target = targetCurrency();
  const rate = fxRate(target);
  const converted = n * rate;
  const loc = locale() || 'pt-BR';

  // Intl cuida de símbolo, separadores e posição automaticamente
  // (ex: USD em en-US → "$1,500.00"; BRL em pt-BR → "R$ 1.500,00").
  try {
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: target,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(converted);
  } catch {
    // Fallback manual usando settings do admin (caso o browser não
    // reconheça o locale ou a moeda).
    const c = storeCurrency();
    const fixed = converted.toFixed(2);
    const [intPart, decPart] = fixed.split('.');
    const intWithThou = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, c.thousand);
    const formatted = intWithThou + c.decimal + decPart;
    return c.position === 'after' ? `${formatted} ${c.symbol}` : `${c.symbol} ${formatted}`;
  }
}

/**
 * Quebra o preço em símbolo + valor (sem casas quando inteiro), respeitando
 * locale/conversão. Para páginas que estilizam moeda/valor/período separados.
 */
export function formatMoneyParts(amount) {
  const n = Number(amount) || 0;
  const target = targetCurrency();
  const converted = n * fxRate(target);
  const loc = locale() || 'pt-BR';
  try {
    const parts = new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: target,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).formatToParts(converted);
    let symbol = '';
    let value = '';
    for (const p of parts) {
      if (p.type === 'currency') symbol += p.value;else if (p.type !== 'literal') value += p.value;
    }
    return {
      symbol,
      value: value.trim()
    };
  } catch {
    const c = storeCurrency();
    return {
      symbol: c.symbol,
      value: String(Math.round(converted * 100) / 100)
    };
  }
}

/** Indica visualmente quando o valor exibido é uma conversão (não o cobrado). */
export function isConvertedDisplay() {
  const target = targetCurrency();
  return target !== storeCurrency().code.toUpperCase();
}
flarum.reg.add('ramon-marketplace', 'common/utils/currency', { currency: currency,formatMoney: formatMoney,formatMoneyParts: formatMoneyParts,isConvertedDisplay: isConvertedDisplay, });